import type { AnalyticsEvent } from '../../../../packages/domain/src/analytics';

/**
 * The Lab's four events, now drawn from the shared vocabulary in `packages/domain/src/analytics.ts`
 * so the names it has been recording since before the app captured anything stay joined to the
 * names the app records now.
 *
 * Lab events carry identifiers and counts only — no prompts, scripts, account emails or media URLs.
 * Prompts and completions are captured, deliberately and in full, but from the executor as
 * `$ai_generation` events rather than from a browser; see `packages/domain/src/analytics.ts` for
 * why that exception exists and `packages/domain/src/policies.ts` for what users are told about it.
 */
type Event = Extract<
  AnalyticsEvent,
  'concept_created' | 'production_requested' | 'cast_locked' | 'episode_accepted'
>;

export async function record(event: Event, properties: Record<string, string | number> = {}) {
  if (import.meta.env.VITE_DEMO_MODE === 'true' || !import.meta.env.VITE_POSTHOG_KEY) return;
  try {
    // The daily ceiling used to be a hard 100. It is now configuration, because the number that
    // protects a free-tier allowance depends on how many people are using the thing, and finding
    // that out should not require a deploy. Unset means no ceiling.
    const cap = Number(import.meta.env.VITE_ANALYTICS_DAILY_CAP ?? 0);
    if (cap > 0) {
      const key = `drama-events-${new Date().toISOString().slice(0, 10)}`;
      const count = Number(localStorage.getItem(key) ?? 0);
      if (count >= cap) return;
      localStorage.setItem(key, String(count + 1));
    }
    const { default: posthog } = await import('posthog-js');
    posthog.capture(event, { surface: 'lab', ...properties });
  } catch {
    /* Analytics never prevents production. */
  }
}
